import { useEffect, useState } from 'react';
import { X, FileText, ExternalLink, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PerfRun, RunInterpretation, getRun, getReportUrl, deleteRun } from '@/services/perfTestsApi';
import { RunInterpretationPanel } from './RunInterpretation';
import { StatusCodesTable } from './StatusCodesTable';

interface Props {
  runId: string | null;
  onClose: () => void;
  onDeleted?: () => void;
}

function fmtMs(ms: number | string | null): string {
  if (ms === null || ms === undefined) return '—';
  const n = Number(ms);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(0) + ' ms';
}

function fmtRate(rate: number | string | null): string {
  if (rate === null || rate === undefined) return '—';
  const n = Number(rate);
  if (!Number.isFinite(n)) return '—';
  return (n * 100).toFixed(2) + ' %';
}

export function RunDetailDrawer({ runId, onClose, onDeleted }: Props) {
  const [run, setRun] = useState<PerfRun | null>(null);
  const [interpretation, setInterpretation] = useState<RunInterpretation | null>(null);
  const [loading, setLoading] = useState(false);
  const [showReport, setShowReport] = useState(false);

  useEffect(() => {
    if (!runId) {
      setRun(null);
      setInterpretation(null);
      setShowReport(false);
      return;
    }
    setLoading(true);
    getRun(runId)
      .then((r) => {
        setRun(r.run);
        setInterpretation(r.interpretation);
      })
      .catch(() => {
        setRun(null);
        setInterpretation(null);
      })
      .finally(() => setLoading(false));
  }, [runId]);

  if (!runId) return null;

  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-2xl bg-white shadow-xl flex flex-col">
        <header className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-2">
          <h2 className="font-semibold text-gray-900">Détail du run</h2>
          <div className="flex items-center gap-1">
            {run && run.status !== 'pending' && run.status !== 'running' && (
              <button
                onClick={async () => {
                  if (!runId) return;
                  if (!confirm('Supprimer ce run ? Le rapport HTML et les samples NDJSON seront aussi effacés.')) return;
                  try {
                    await deleteRun(runId);
                    toast.success('Run supprimé');
                    onDeleted?.();
                    onClose();
                  } catch {
                    toast.error('Suppression échouée');
                  }
                }}
                title="Supprimer ce run"
                className="p-1 text-red-600 hover:bg-red-50 rounded flex items-center gap-1 text-xs"
              >
                <Trash2 className="w-4 h-4" />
                Supprimer
              </button>
            )}
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
              <X className="w-5 h-5" />
            </button>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto p-4">
          {loading && <div className="text-sm text-gray-500">Chargement…</div>}
          {!loading && !run && <div className="text-sm text-red-600">Run introuvable</div>}
          {!loading && run && (
            <div className="space-y-4">
              {interpretation && <RunInterpretationPanel interpretation={interpretation} />}
              <KV label="ID" value={<code className="text-xs">{run.id}</code>} />
              <KV label="Scénario" value={run.scenario_name} />
              <KV label="Profil" value={run.profile} />
              <KV label="Cible" value={<code className="text-xs">{run.target_url}</code>} />
              <KV label="Status" value={run.status} />
              <KV label="Démarré" value={new Date(run.started_at).toLocaleString('fr-FR')} />
              {run.finished_at && (
                <KV label="Fini" value={new Date(run.finished_at).toLocaleString('fr-FR')} />
              )}
              <KV label="Durée" value={run.duration_ms ? `${(run.duration_ms / 1000).toFixed(1)} s` : '—'} />
              <hr />
              <h3 className="text-sm font-medium text-gray-700">Métriques</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <KV label="p95 CRUD" value={fmtMs(run.p95_crud_ms)} />
                <KV label="p95 Aggreg" value={fmtMs(run.p95_aggregation_ms)} />
                <KV label="p95 LLM" value={fmtMs(run.p95_llm_ms)} />
                <KV label="p95 Auth" value={fmtMs(run.p95_auth_ms)} />
                <KV label="p95 SSE (TTFB)" value={fmtMs(run.p95_sse_ms)} />
                <KV label="Taux d'erreur" value={fmtRate(run.http_req_failed_rate)} />
                <KV label="Total reqs" value={run.total_requests ?? '—'} />
                <KV label="Itérations" value={run.total_iterations ?? '—'} />
              </div>

              <hr />
              <h3 className="text-sm font-medium text-gray-700 flex items-center justify-between">
                <span>Status codes par tag</span>
                <span className="text-xs font-normal text-gray-500">
                  Codes 4xx/5xx en couleur
                </span>
              </h3>
              <StatusCodesTable statusCodes={run.status_codes_summary} />

              {run.error_message && (
                <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700">
                  <strong>Erreur :</strong> {run.error_message}
                </div>
              )}
              {run.cancellation_reason && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-700">
                  <strong>Annulation :</strong> {run.cancellation_reason}
                </div>
              )}

              {run.report_html_path && (
                <div className="space-y-2">
                  <hr />
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-gray-700 flex items-center gap-1">
                      <FileText className="w-4 h-4" />
                      Rapport k6
                    </h3>
                    <a
                      href={getReportUrl(run.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-indigo-600 hover:underline flex items-center gap-1"
                    >
                      Ouvrir dans un onglet
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <button
                    onClick={() => setShowReport((v) => !v)}
                    className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
                  >
                    {showReport ? 'Masquer' : 'Afficher'} le rapport iframe
                  </button>
                  {showReport && (
                    <iframe
                      src={getReportUrl(run.id)}
                      title={`Report ${run.id}`}
                      className="w-full h-[600px] border border-gray-200 rounded"
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-2 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-900 text-right">{value}</span>
    </div>
  );
}
